import { TRACE_LEVELS, type TraceConfig, type TraceEvent, type TraceLevel } from '@ai-studio/shared';
import { create } from 'zustand';

import { pushRing, type TraceRow } from '../lib/game-trace.js';

/**
 * The Play window's game trace as the editor sees it: a client ring buffer
 * for the Game console panel, the trace config (level picker, also set by the
 * assistant's game_trace_config) and whether the engine bundle supports it.
 */

export const CLIENT_TRACE_LIMIT = 20_000;
const CONFIG_KEY = 'prism.traceConfig';

export type TraceSupport = 'unknown' | 'supported' | 'unsupported';

interface GameTraceStore {
  rows: TraceRow[];
  /** Rows the ring buffer pushed out since the last clear. */
  overflow: number;
  config: TraceConfig;
  support: TraceSupport;
  /** Current Play session id (null when not playing). */
  session: string | null;
  /** Upload failures / drops the server never got. */
  unsent: number;
  append: (events: readonly TraceEvent[]) => void;
  clear: () => void;
  setConfig: (config: TraceConfig) => void;
  setLevel: (level: TraceLevel) => void;
  setSupport: (support: TraceSupport) => void;
  setSession: (session: string | null) => void;
  addUnsent: (count: number) => void;
}

export function parseTraceConfig(raw: string | null): TraceConfig {
  if (!raw) return { level: 'normal' };
  try {
    const parsed = JSON.parse(raw) as Partial<TraceConfig>;
    if (!parsed || typeof parsed !== 'object' || !(TRACE_LEVELS as readonly string[]).includes(String(parsed.level)))
      return { level: 'normal' };
    return parsed as TraceConfig;
  } catch {
    return { level: 'normal' };
  }
}

function readConfig(): TraceConfig {
  try {
    return parseTraceConfig(typeof localStorage === 'undefined' ? null : localStorage.getItem(CONFIG_KEY));
  } catch {
    return { level: 'normal' };
  }
}

function writeConfig(config: TraceConfig): void {
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(CONFIG_KEY, JSON.stringify(config));
  } catch {
    // Storage blocked: the config still applies to this session.
  }
}

let nextKey = 1;

export const useGameTrace = create<GameTraceStore>((set) => ({
  rows: [],
  overflow: 0,
  config: readConfig(),
  support: 'unknown',
  session: null,
  unsent: 0,
  append: (events) => {
    if (events.length === 0) return;
    const add = events.map((e) => ({ ...e, key: nextKey++ }));
    set((s) => {
      const rows = pushRing(s.rows, add, CLIENT_TRACE_LIMIT);
      return { rows, overflow: s.overflow + Math.max(0, s.rows.length + add.length - rows.length) };
    });
  },
  clear: () => set({ rows: [], overflow: 0 }),
  setConfig: (config) => {
    writeConfig(config);
    set({ config });
  },
  setLevel: (level) =>
    set((s) => {
      const config = { ...s.config, level };
      writeConfig(config);
      return { config };
    }),
  setSupport: (support) => set((s) => (s.support === support ? s : { support })),
  setSession: (session) => set({ session }),
  addUnsent: (count) => set((s) => ({ unsent: s.unsent + count })),
}));
